import type { User } from 'firebase/auth';
import { functionHeaders } from './functionSecurity';

const ERROR_COPY: Record<string, string> = {
  sign_in_required: 'Sign in again before saving this class.',
  not_allowed: 'Your account cannot make that change to this class.',
  bad_request: 'That class change could not be saved. Refresh and try again.',
  conflict: 'This class changed while you were saving. Refresh and try again.',
  paired_seat_not_current: 'This linked sub cover is no longer current. Refresh and review the cover before keeping it permanently.',
  // Keyed by the codes lib/offeringWrite.mjs actually throws (refute round on
  // #961: a not_found key here was dead copy, and the deleted-class case got the
  // generic retry line). The client test cross-checks every server code.
  offering_not_found: 'This class no longer exists.',
  music_link_not_found: 'This music link no longer exists. Refresh the class and try again.',
  teacher_not_found: 'That teacher account is no longer available.',
  media_cap_reached: 'This class has reached its media link limit.',
  // H-3 (security-audit-2026-09-16): a video-upload reservation resolved to
  // something other than a video (the magic-byte check on a lane that only
  // ever accepts video here).
  video_required: 'That upload was not a video file. Choose a video and try again.',
  // The weekly-time actions (lib/classSeriesWrite.mjs, bug B9).
  series_not_found: 'That weekly time no longer exists. Refresh the page.',
  room_not_found: 'That room no longer exists. Pick another room.',
  reservation_not_in_series: 'One session could not be taken off because it is not an upcoming session of this weekly time. Refresh the page and try again.',
  future_sessions_remain: 'This weekly time still has upcoming sessions on the calendar, so it was not removed. Tap Remove again to finish.',
  legacy_teacher_listing: 'This class still lists you as a teacher from an older setup, so its teaching team needs the Director. Ask the Director to save it, then try again.',
  session_fits_schedule: 'That session still fits this weekly time, so it stays. To call off one night, use Cancel a class session.',
  sessions_not_released: 'Some upcoming sessions of this weekly time were already taken off, but not all of them. Tap Save time or Remove again to finish.',
  // The Jr assistant action (setOfferingAssistant, bug B10).
  not_jr_staff: 'Only an active Jr Staff can be attached as a Jr assistant. Ask the Director to check her account, then try again.',
  assistants_changed: 'Someone changed this class\'s Jr assistants while you were editing. Close and open it again.',
  sub_seat_held: 'She is covering this class on a sub seat, and only the Director changes a sub seat. Ask the Director.',
  assistant_limit: 'This class already has 12 Jr assistant sign-ins attached. Take one off before adding another.',
  // Deleted classes (bug B22, lib/deletedClasses.mjs).
  workshop_class: 'This class belongs to a workshop, so it is not deleted from Classes. Open Workshops to close its signups or move it to Past.',
  deleted_copy_waiting: 'An earlier deleted class with this same id is still in Recently deleted classes. Put that one back or wait for it to clear, then try again.',
  no_saved_copy: 'There is no saved copy of this class, so it cannot be put back. Refresh the page.',
  class_not_clear: 'Something is still on this class (a dancer or an upcoming booking), so the class itself was not deleted. Refresh the page and delete again.',
  already_restored: 'This class was already put back. Refresh the page.',
  delete_superseded: 'This delete was stopped, or the class was put back, while it was running, so nothing more was taken off. Refresh the page and check the class.',
  class_delete_running: 'This class is being deleted right now, maybe on another device. Wait for that to finish, then refresh and try again.',
  delete_lapsed: 'This delete waited too long between steps and was stopped, so the class was not deleted. What it had already taken off is saved: Put back on its row in Studio Settings, Recently deleted classes, returns it, or delete the class again and the new delete keeps it.',
  copy_clearing: 'An old saved copy of this class is being cleared right now. Try again in a minute.',
  nothing_came_off: 'Nothing came off this class before its delete stopped, so there is nothing to put back. Refresh the page.',
  deleted_class_expired: 'This class was deleted more than 30 days ago, so it can no longer be put back.',
  class_exists: 'A class with this same id is on Classes now, so this one cannot be put back over it.',
};

/**
 * THE STAFF VIDEO LANE'S OWN SENTENCES (Bobbie, 2026-09-23 9:17pm Central).
 *
 * She added a video on a class's Music & Video tab and was told, in red,
 * "The class could not be saved. Please try again." Her video had in fact
 * saved, from an earlier try of the same file, and the server was refusing
 * the retry as attachment_already_used. This file had no sentence for that
 * code, so it fell to the class fallback: a false statement about the wrong
 * thing, and a next step, try again, that could only fail the same way.
 *
 * appendMediaLink (kind video) and finalizeVideoUpload answer from this map
 * first, then ERROR_COPY, then VIDEO_FALLBACK, and none of them says "the
 * class" about a problem that was the video's. The attachment codes are the
 * ones lib/offeringWrite.mjs forwards from the upload gateway
 * (STAFF_VIDEO_ATTACHMENT_CODES there). The key list is not typed from
 * memory: tests/offering-write-client.test.mjs walks the server's own source
 * for every code this lane can answer and fails when one lands on the
 * fallback, which is the check the older ERROR_COPY test could not make,
 * because a forwarded code is never a literal in the server file.
 */
//
// NEVER CLAIM AN OUTCOME THE PAGE CANNOT KNOW (refute round one on #1215,
// P2-2). The first fallback here said "The video did not save" for any answer
// that was not the function's JSON, and a platform 502 AFTER a committed save
// is exactly that: the video and its family post were on the list while the
// page said they were not, the same false statement this map exists to end,
// pointing the other way. So the two sentences below that can follow a save
// that landed say only what is known, and send her to the Videos list before
// she tries again. offering_write_failed shares that care: a transaction
// whose commit answer was lost can throw after it landed.
const VIDEO_SERVER_FAULT =
  'Something went wrong on our side, not yours. Refresh the page and check the Videos list, then add the video again if it is not there.';
const VIDEO_NOT_FINISHED =
  'That video did not finish uploading. Choose the same video again to send it.';
const VIDEO_ERROR_COPY: Record<string, string> = {
  // Forwarded from the upload gateway (lib/offeringWrite.mjs,
  // forwardAttachmentError). not_ready and not_finalized both mean the bytes
  // never finished their proof; the stored request id survives that, so the
  // same video picked again resumes the same upload.
  attachment_not_ready: VIDEO_NOT_FINISHED,
  upload_not_finalized: VIDEO_NOT_FINISHED,
  // Since the same-video retry is answered with the row it already made,
  // this only comes back when that row is gone. The stored request id is
  // cleared on this code (staffClassVideoUpload.ts, Offering.tsx), so the
  // next pick really does start a fresh upload, which is what this promises.
  attachment_already_used:
    'That upload was already saved here once. Choose the video again and it will send as a fresh upload.',
  video_required: 'That upload was not a video file. Choose a video and try again.',
  // The lane's own refusals, worded for a video.
  bad_request: 'That video could not be saved. Refresh the page and add it again.',
  not_allowed: 'Your account cannot add videos to this class. Sign in with your staff account and try again.',
  conflict:
    'The Videos list changed while this one was saving. Refresh the page, check the list, and add it again if it is not there.',
  offering_not_found: 'This class no longer exists, so the video has nowhere to go.',
  media_cap_reached:
    'This class already has as many videos as it can hold. Remove an old one, then add this one again.',
  // The handler's own answers, before the lane ever runs.
  sign_in_required: 'Your sign-in ran out. Sign in again, then add the video again.',
  origin_invalid: 'Refresh the page, then add the video again.',
  'missing app check token': 'Refresh the Hub, then add the video again.',
  'invalid app check token': 'Refresh the Hub, then add the video again.',
  method_not_allowed: VIDEO_SERVER_FAULT,
  server_not_configured: VIDEO_SERVER_FAULT,
  offering_write_failed: VIDEO_SERVER_FAULT,
};
const VIDEO_FALLBACK =
  'We could not tell whether that video saved. Refresh the page and check the Videos list before you add it again.';

type OfferingWriteResult = {
  /** finishClassDelete: when Undo ends and when the copy is removed, in ms. */
  undoUntil?: number | null;
  purgeAfter?: number | null;
  /** listDeletedClasses. */
  classes?: unknown[];
  notificationPostId?: string | null;
  notificationOperationId?: string | null;
  replayed?: boolean;
  /** removeMediaLink: how many entries left the list, and how many posts lost a clip. */
  removed?: number;
  stripped?: number;
  /** createSeries: the new weekly time's id. */
  seriesId?: string | null;
  /** updateSeriesSchedule / deleteSeries with releaseSessions: the sessions taken off. */
  released?: string[];
};

/** The sentence a server code earns on one lane. Never a raw code. */
export function offeringWriteErrorMessage(code: string, lane: 'class' | 'video'): string {
  if (lane === 'video') return VIDEO_ERROR_COPY[code] ?? ERROR_COPY[code] ?? VIDEO_FALLBACK;
  return ERROR_COPY[code] || 'The class could not be saved. Please try again.';
}

/**
 * The thrown error keeps the server's code beside the sentence, so a caller
 * that has to act on one outcome (the stored upload id on
 * attachment_already_used) can read it instead of matching copy.
 */
export class OfferingWriteClientError extends Error {
  readonly code: string;
  /** sessions_not_released: the sessions the server already took off. */
  readonly released: string[];
  constructor(code: string, lane: 'class' | 'video', released: string[] = []) {
    super(offeringWriteErrorMessage(code, lane));
    this.name = 'OfferingWriteClientError';
    this.code = code;
    this.released = released;
  }
}

/** The sessions already taken off, read off a thrown weekly-time error. */
export function releasedIdsFromError(error: unknown): string[] {
  return error instanceof OfferingWriteClientError ? [...error.released] : [];
}

async function writeOffering(
  user: User,
  body: Record<string, unknown>,
  lane: 'class' | 'video' = 'class'
): Promise<OfferingWriteResult> {
  const response = await fetch('/.netlify/functions/offeringWrite', {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: await functionHeaders(user), body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null) as { ok?: boolean; error?: string; released?: unknown } | null;
  if (!response.ok || payload?.ok !== true) {
    const released = Array.isArray(payload?.released)
      ? payload.released.filter((value): value is string => typeof value === 'string')
      : [];
    throw new OfferingWriteClientError(typeof payload?.error === 'string' ? payload.error : '', lane, released);
  }
  return payload as OfferingWriteResult;
}

/**
 * WEEKLY TIMES FOR A MANAGER (bug B9). classSeries stays Director-only in the
 * client rules, so a Manager's add, move and remove go through the
 * offeringWrite Function (lib/classSeriesWrite.mjs). The Director's screen
 * still writes directly, exactly as before.
 */
export async function createClassSeriesViaServer(user: User, input: Record<string, unknown>): Promise<string> {
  const result = await writeOffering(user, { action: 'createSeries', input });
  if (typeof result.seriesId !== 'string' || !result.seriesId) {
    throw new OfferingWriteClientError('', 'class');
  }
  return result.seriesId;
}

/**
 * releaseSessions: the server takes this time's upcoming class sessions off
 * in the same call (a Manager's move and remove, which the server refuses
 * her without it). That is the same power as Remove and add the time again,
 * and quiet like the Director's own edit. The single-session action is the
 * Director's alone. A release that stops part way answers
 * sessions_not_released with the ids already taken off (`released` on the
 * thrown error); the same save again finishes it.
 */
export function updateClassSeriesScheduleViaServer(
  user: User,
  seriesId: string,
  schedule: { roomId: string; weekday: number; weekPattern: string; startTime: string; endTime: string },
  options: { releaseSessions?: boolean } = {}
): Promise<string[]> {
  return writeOffering(user, {
    action: 'updateSeriesSchedule', seriesId, schedule,
    ...(options.releaseSessions ? { releaseSessions: true } : {}),
  }).then((result) => (Array.isArray(result.released) ? result.released : []));
}

export function deleteClassSeriesViaServer(
  user: User,
  seriesId: string,
  options: { releaseSessions?: boolean } = {}
): Promise<string[]> {
  return writeOffering(user, {
    action: 'deleteSeries', seriesId,
    ...(options.releaseSessions ? { releaseSessions: true } : {}),
  }).then((result) => (Array.isArray(result.released) ? result.released : []));
}

export function createOfferingViaServer(user: User, offeringId: string, input: Record<string, unknown>): Promise<void> {
  return writeOffering(user, { action: 'create', offeringId, input }).then(() => undefined);
}

export function updateOfferingViaServer(
  user: User,
  offeringId: string,
  fields: Record<string, unknown>,
  options: { assistantBase?: string[] } = {}
): Promise<void> {
  // AboutPanel stamps every family note save; Offering's detail editor stamps
  // only a save that changes the words (familyNoteSaveMovesNote,
  // DECISIONS 159), so an internal note edit beside it never makes an old
  // note look new. Presence, not the note text itself, carries that choice
  // without serializing a sentinel.
  const stampFamilyNote = Object.prototype.hasOwnProperty.call(fields, 'familyNoteUpdatedAt') && Object.prototype.hasOwnProperty.call(fields, 'familyNote');
  const serverOwned = new Set(['familyNoteUpdatedAt', 'updatedAt', 'createdAt', 'createdBy', 'teacherUids', 'teacherNames']);
  const patch = Object.fromEntries(Object.entries(fields).filter(([key]) => !serverOwned.has(key)));
  // assistantBase: the Jr assistants the class editor opened on (B10 refute
  // round 1). The server answers assistants_changed when the stored list has
  // moved since, so a save from a stale form never undoes someone's attach.
  return writeOffering(user, {
    action: 'update', offeringId, patch, stampFamilyNote,
    ...(options.assistantBase ? { assistantBase: options.assistantBase } : {}),
  }).then(() => undefined);
}

/**
 * Attach or take off ONE Jr assistant (bug B10): the Director, a Manager and
 * front desk, on any class. The server checks that anyone front desk or a
 * Manager adds is an active Jr Staff, treats her two sign-ins as one person,
 * and refreshes who can see the class's bookings after a change.
 */
export function setOfferingAssistantViaServer(
  user: User,
  offeringId: string,
  assistantUid: string,
  assigned: boolean,
  assistantName: string | null
): Promise<void> {
  return writeOffering(user, {
    action: 'setAssistant', offeringId, assistantUid, assigned, assistantName,
  }).then(() => undefined);
}

export function promoteOfferingSubSeatViaServer(user: User, offeringId: string, seatUid: string): Promise<void> {
  return writeOffering(user, { action: 'promoteSubSeat', offeringId, seatUid }).then<void>(() => undefined);
}

export function appendOfferingMediaLinkViaServer(user: User, offeringId: string, kind: 'music' | 'video', link: unknown): Promise<void> {
  return writeOffering(
    user, { action: 'appendMediaLink', offeringId, kind, link }, kind === 'video' ? 'video' : 'class'
  ).then(() => undefined);
}

/**
 * Take VIDEO entries off a class's Music & Videos through the server, which
 * also takes the clip back off the class post each one was mirrored from
 * (Bobbie's mirror ruling 2, 2026-09-09: "Remove both ways"). The post's words
 * stay. Music removal is unchanged and still goes through its own lane.
 */
export function removeOfferingMediaLinkViaServer(
  user: User,
  offeringId: string,
  linkIds: string[]
): Promise<OfferingWriteResult> {
  return writeOffering(user, { action: 'removeMediaLink', offeringId, kind: 'video', linkIds });
}

export function finalizeOfferingVideoUploadViaServer(
  user: User,
  offeringId: string,
  reservationId: string,
  link: unknown
): Promise<OfferingWriteResult> {
  return writeOffering(user, { action: 'finalizeVideoUpload', offeringId, reservationId, link }, 'video');
}

export class MusicSaveError extends Error {
  readonly code: string;
  constructor(code: string) {
    const messages: Record<string, string> = {
      cannot_approve_own_music: 'You cannot mark your own music addition as the final cut. Ask another staff member to approve it.',
      music_uploader_unverified: 'This older upload has no verified uploader. Add it again before another staff member marks it final.',
      music_changed: 'The music changed while you were editing. Please try again.',
      music_asset_requires_new_upload: 'Add a replacement file as a new upload so its uploader can be verified.',
      music_upload_incomplete: 'Wait for the music upload to finish before marking it final.',
      media_cap_reached: 'This class has reached its music link limit.',
    };
    super(messages[code] ?? ERROR_COPY[code] ?? 'The music could not be saved. Please try again.');
    this.code = code;
  }
}

export async function writeOfferingMusicViaServer(user: User, body: Record<string, unknown>): Promise<{ links?: unknown[]; revision?: string }> {
  const response = await fetch('/.netlify/functions/offeringWrite', {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: await functionHeaders(user), body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok !== true) throw new MusicSaveError(payload?.error ?? 'music_save_failed');
  return payload;
}

/**
 * DELETED CLASSES (bug B22). Bobbie, 2026-08-06: "I want an undo button to be
 * present for 1 hour.... and then a place to completly undo maybe under
 * studio settings.... but only up to a month". The server keeps a copy of the
 * class before its delete starts, deletes the class last, lists the copies
 * and puts one back. Director only (lib/deletedClasses.mjs).
 */
export interface ArchiveClassForDeleteAnswer {
  /** This delete's id: passed back to keep its copy current, or to close it if the delete stops. */
  attemptId: string | null;
  /** A retry after a lost answer: the class was already deleted, and this is its Undo. */
  alreadyDeleted: boolean;
  undoUntil: number | null;
}

/** What a delete is ABOUT to take off, told to its copy first (write ahead). */
export interface ClassDeleteTakeOff {
  studentIds?: readonly string[];
  seriesIds?: readonly string[];
  reservationIds?: readonly string[];
}

/**
 * Start a delete (no attemptId): the server keeps a copy before anything
 * comes off and answers this delete's attempt id. Or, with the attempt id and
 * `takeOff`, tell that copy what is about to come off BEFORE it does, which
 * also renews the delete's lease; refused once the delete was closed, and
 * then nothing more may come off.
 */
export function archiveClassForDeleteViaServer(
  user: User,
  offeringId: string,
  attemptId?: string,
  takeOff?: ClassDeleteTakeOff,
  /** What came off since this delete's last report: the copy keeps it for sure. */
  confirmed?: ClassDeleteTakeOff
): Promise<ArchiveClassForDeleteAnswer> {
  return writeOffering(user, {
    action: 'archiveClassForDelete',
    offeringId,
    ...(attemptId
      ? {
        attemptId,
        takeOff: {
          studentIds: [...(takeOff?.studentIds ?? [])],
          seriesIds: [...(takeOff?.seriesIds ?? [])],
          reservationIds: [...(takeOff?.reservationIds ?? [])],
        },
        confirmed: {
          studentIds: [...(confirmed?.studentIds ?? [])],
          seriesIds: [...(confirmed?.seriesIds ?? [])],
          reservationIds: [...(confirmed?.reservationIds ?? [])],
        },
      }
      : {}),
  }).then((result) => {
    const r = result as unknown as Record<string, unknown>;
    return {
      attemptId: typeof r.attemptId === 'string' ? r.attemptId : null,
      alreadyDeleted: r.alreadyDeleted === true,
      undoUntil: typeof r.undoUntil === 'number' ? r.undoUntil : null,
    };
  });
}

/** The delete stopped: the server keeps a copy of only what came off, or drops it. */
export function stopClassDeleteViaServer(user: User, offeringId: string, attemptId: string): Promise<void> {
  return writeOffering(user, { action: 'stopClassDelete', offeringId, attemptId }).then(() => undefined);
}

export function finishClassDeleteViaServer(
  user: User,
  offeringId: string,
  releasedStudentIds: readonly string[],
  attemptId: string
): Promise<{ undoUntil: number | null; purgeAfter: number | null }> {
  return writeOffering(user, {
    action: 'finishClassDelete', offeringId, releasedStudentIds: [...releasedStudentIds], attemptId,
  }).then((result) => ({
    undoUntil: typeof result.undoUntil === 'number' ? result.undoUntil : null,
    purgeAfter: typeof result.purgeAfter === 'number' ? result.purgeAfter : null,
  }));
}

export function listDeletedClassesViaServer(user: User): Promise<unknown[]> {
  return writeOffering(user, { action: 'listDeletedClasses' })
    .then((result) => (Array.isArray(result.classes) ? result.classes : []));
}

export function restoreDeletedClassViaServer(user: User, offeringId: string): Promise<Record<string, unknown>> {
  return writeOffering(user, { action: 'restoreDeletedClass', offeringId })
    .then((result) => result as unknown as Record<string, unknown>);
}
